import { Controller } from "@hotwired/stimulus"

// Tracks form dirty state and shows a sticky save bar when the inline
// submit button scrolls out of view. Supports cancel (form.reset()).
//
// Usage:
//   <div data-controller="dirty-form">
//     <form data-action="submit->dirty-form#submitted" id="my-form">
//       ...
//       <div data-dirty-form-target="anchor">
//         <button type="submit" data-dirty-form-target="submit" disabled>Save</button>
//       </div>
//     </form>
//     <div data-dirty-form-target="stickyBar" class="fixed ... opacity-0 translate-y-2 pointer-events-none">
//       <button data-action="dirty-form#cancel">Cancel</button>
//       <button type="submit" form="my-form">Save</button>
//     </div>
//   </div>

export default class extends Controller {
  static targets = ["submit", "anchor", "stickyBar"]

  connect() {
    this._isDirty = false
    this._anchorVisible = true
    this._form = this.element.tagName === "FORM"
      ? this.element
      : this.element.querySelector("form")

    this._onChange = this._onChange.bind(this)
    this._form.addEventListener("input", this._onChange)
    this._form.addEventListener("change", this._onChange)

    if (this.hasAnchorTarget) {
      this._observer = new IntersectionObserver(entries => {
        this._anchorVisible = entries[0].isIntersecting
        this._updateStickyBar()
      }, { threshold: 0.5 })
      this._observer.observe(this.anchorTarget)
    }
  }

  disconnect() {
    this._form?.removeEventListener("input", this._onChange)
    this._form?.removeEventListener("change", this._onChange)
    this._observer?.disconnect()
  }

  cancel() {
    this._form.reset()
    this._markClean()
  }

  submitted() {
    this._markClean()
  }

  _onChange() {
    if (!this._isDirty) {
      this._isDirty = true
      this.submitTargets.forEach(el => el.disabled = false)
    }
    this._updateStickyBar()
  }

  _markClean() {
    this._isDirty = false
    this.submitTargets.forEach(el => el.disabled = true)
    this._updateStickyBar()
  }

  _updateStickyBar() {
    if (!this.hasStickyBarTarget) return
    const show = this._isDirty && !this._anchorVisible
    this.stickyBarTarget.classList.toggle("opacity-0", !show)
    this.stickyBarTarget.classList.toggle("translate-y-2", !show)
    this.stickyBarTarget.classList.toggle("pointer-events-none", !show)
  }
}
